import { useCallback, useEffect, useState } from "react";
import { AppConfigurationExtensionContext, InstallationData } from "../contexts/appConfigurationExtensionContext";
import { useMarketPlaceApp } from "../hooks/useMarketPlaceApp";
import { decryptAppConfig, encryptAppConfig, validateIfNeedsEncryption } from "../../shared-components/utils/common";
import { getAIAAPIEndpoint, removeDataFromLocalStorage, areKeysMasked } from "../../shared-components/utils/helper";
import axios from "axios";
import { isEmpty } from "lodash";
import { APP_TYPE } from "../../shared-components/constant";

export const AppConfigurationExtensionProvider = ({ children }: any) => {
  const [installationData, setInstallation] = useState<InstallationData>({
    configuration: {},
    serverConfiguration: {},
  });
  const [appUpdated, setAppUpdated] = useState<any>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const { location, appSdk, appRegion, appConfig } = useMarketPlaceApp();
  const [token, setToken] = useState<string>('');

  const refreshToken = async (): Promise<string> => {
    // If token already exists, return it. Otherwise, fetch it.
    if (!token) {
      const newToken = await getTokenFromBackend();
      setToken(newToken); // This updates the context state so that all components have access to it.
      return newToken;
    }
    return token;
  };

  const getTokenFromBackend = async () => {
    const url = new URL(window.location.href);
    const response = await axios.post(`${getAIAAPIEndpoint(appRegion)}/v1/exchangeToken`, {
      token: url.searchParams.get("app-token"),
    });
    return response.data.data.token as string;
  };

  useEffect(() => {
    if (!appSdk || !appRegion) return
    const initialiser = async () => {
      var appConfigWidget = appSdk?.location.AppConfigWidget;
      setLoading(true);
      const config = await appConfigWidget?.installation.getInstallationData();
      // @ts-ignore
      const appName = appConfigWidget?.installation?._data?.manifest?.name;

      if (config?.configuration && appName) {
        const appType =
          appName.toLowerCase().includes("image assistant")
            ? APP_TYPE.IMAGE_GENERATION
            : APP_TYPE.TEXT_GENERATION;
        config.configuration["appType"] = appType;
      }

      if (config?.configuration?.mode === 'managed_by_user') {
        refreshToken().then(async (token) => {
          const decryptedConfig = await decryptAppConfig(appRegion, token, config.configuration.config, config.configuration.provider.value);
          config.configuration.config = decryptedConfig;
          setInstallation((config as any))
          setLoading(false);
        }).catch((e) => {
          console.error('App Config needs to be signed');
          if (!isEmpty(config.configuration.config.keys)) {
            setAppUpdated(false);
          }
          setLoading(false)
        })
      } else {
        setInstallation((config as any))
        setLoading(false)
      }
    }
    initialiser()
  }, [appSdk, appRegion])

  const setInstallationData = useCallback(
    async (installationUpdate: InstallationData) => {
      setLoading(true);
      try {
        if (!appRegion) {
          throw new Error('App region is not available')
        }
        const configuration = { ...installationData.configuration, ...installationUpdate.configuration }
        let encrypted_configuration = JSON.parse(JSON.stringify(configuration));
        const isBrandKitEnabled = encrypted_configuration.isBrandKitEnabled;
        if (encrypted_configuration?.mode === 'managed_by_user' && validateIfNeedsEncryption(encrypted_configuration.config)) {
          if (encrypted_configuration.isBrandKitEnabled || 
              areKeysMasked(encrypted_configuration.config,encrypted_configuration.provider.value)) {
            encrypted_configuration = appConfig;
            if (isBrandKitEnabled) {
              encrypted_configuration.isBrandKitEnabled = true;
            }
          }
          else {
            const token = await refreshToken();
            const encryptedConfig = await encryptAppConfig(appRegion, token, encrypted_configuration.config, encrypted_configuration.provider.value);
            encrypted_configuration.config = encryptedConfig
          }
        }
        const newInstallationData: InstallationData = {
          configuration: encrypted_configuration,
          serverConfiguration: installationData.serverConfiguration,
        };
        await location.installation.setInstallationData(newInstallationData);
        setInstallation({ configuration, serverConfiguration: installationData.serverConfiguration });
        if (appSdk) {
          removeDataFromLocalStorage(appSdk.installationUID)
        }
        setLoading(false);
      } catch(err) {
        console.error('Failed to set installation', err);
        setLoading(false)
      }
    },
    [location, setInstallation, setLoading, appRegion]
  );

  return (
    <AppConfigurationExtensionContext.Provider
      value={{ appUpdated, installationData, setInstallationData, loading, token, refreshToken }}>
      {children}
    </AppConfigurationExtensionContext.Provider>
  );
};
